import type { ToolCall } from '../../types'

const ADD_PREFERENCE = 'add_skill_preference'
const EDIT_PREFERENCE = 'edit_skill_preference'
const REMOVE_PREFERENCE = 'remove_skill_preference'
const SKILL_PREFERENCE_TOOLS = new Set<string>([ADD_PREFERENCE, EDIT_PREFERENCE, REMOVE_PREFERENCE])

export function isSkillPreferenceTool(toolName: string): boolean {
  return SKILL_PREFERENCE_TOOLS.has(toolName)
}

/** Settings deeplink that opens one skill and scrolls to its preference list. */
export function skillPreferencesDeeplink(skillId: string): string {
  return `highlight://settings/skills?skillId=${encodeURIComponent(skillId)}&section=preferences`
}

function parseMaybeJson(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
    } catch {
      return null
    }
  }
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

export function readStringField(value: unknown, key: string): string | null {
  const field = parseMaybeJson(value)?.[key]
  return typeof field === 'string' && field.trim() ? field.trim() : null
}

export function isPreferenceReviewHandoff(toolCall: Pick<ToolCall, 'result'>): boolean {
  return readStringField(toolCall.result, 'blocked') === 'unread-preferences'
}

export function preferenceWriteFailed(toolCall: Pick<ToolCall, 'result' | 'status' | 'error'>): boolean {
  return toolCall.status === 'failed' || !!toolCall.error || !!readStringField(toolCall.result, 'error')
}

export function getPreferenceWriteError(toolCall: Pick<ToolCall, 'result'>): string {
  if (readStringField(toolCall.result, 'blocked') === 'role-preference') {
    return 'This preference comes from your role and cannot be edited or removed.'
  }
  return 'Highlight could not save this preference.'
}

export function readPreferenceContent(toolCall: Pick<ToolCall, 'result' | 'toolInput'>): string | null {
  const stored = readStringField(parseMaybeJson(toolCall.result)?.preference, 'content')
  return stored ?? readStringField(toolCall.toolInput, 'content')
}

export function getPreferenceCardTitle(toolName: string, skillName: string | null, failed: boolean): string {
  const target = skillName ? `your ${skillName} preferences` : 'skill preferences'
  if (failed) return `Couldn’t update ${target}`

  switch (toolName) {
    case EDIT_PREFERENCE:
      return `Updated ${target}`
    case REMOVE_PREFERENCE:
      return `Removed an entry from ${target}`
    default:
      return `Saved to ${target}`
  }
}
